import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { LanguageDialogComponent } from './language-dialog/language-dialog.component';
import { TenantDialogComponent } from './tenant-dialog/tenant-dialog.component';
import { DomainType, GeneralService } from './general.service';
import { ButtonComponent } from '@akostm/orbit-ui';
import { TenantDomain } from '@clientwire/api';
import { ToastrService } from 'ngx-toastr';
import { DomainDialogService } from './domain-dialog/domain-dialog.service';
import { DomainDialogComponent } from './domain-dialog/domain-dialog.component';

@Component({
  selector: 'wire-general',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
})
export class GeneralComponent implements OnInit {
  readonly #dialog = inject(Dialog);
  readonly #translateService = inject(TranslateService);
  readonly #fb = inject(FormBuilder);
  readonly #generalService = inject(GeneralService);
  readonly #domainService = inject(DomainDialogService)

  readonly #toastr = inject(ToastrService);

  readonly tenantLoading = this.#generalService.tenantLoading;
  readonly languageLoading = this.#generalService.languageLoading;
  readonly domainsLoading = this.#domainService.domainsLoading;
  readonly availableLanguages = this.#generalService.availableLanguages;
  readonly tenant = this.#generalService.tenant;
  readonly domains = this.#domainService.domains;

  readonly editIcon = faEdit;
  readonly plusIcon = faPlus;
  readonly trashIcon = faTrash;

  // Default to client app domain when adding new domains
  readonly customDomainType: DomainType = 'client';

  ngOnInit(): void {
    this.#generalService.loadTenantInfo();
  }

  constructor() {
    effect(() => {
      if (this.tenant()) this.#domainService.loadDomains();
    });
  }


  getLanguageDisplayName(langCode: string | null | undefined): string {
    if (!langCode) {
      langCode = this.#translateService.currentLang || this.#translateService.getDefaultLang();
    }
    const lang = this.availableLanguages().find((l) => l.code === langCode);
    return lang ? lang.name : langCode;
  }

  getSystemDomains(): TenantDomain[] {
    return this.#domainService.getSystemDomains();
  }

  getCustomDomains(): TenantDomain[] {
    return this.#domainService.getCustomDomains();
  }

  getCustomDomainForType(isClientApp: boolean): TenantDomain | undefined {
    return this.#domainService.getCustomDomainByType(isClientApp);
  }

  getDomainStatusClass(status: string): string {
    switch (status) {
      case 'CREATED':
        return 'status-created';
      case 'REQUESTED':
        return 'status-requested';
      case 'DELETE_REQUESTED':
        return 'status-delete-requested';
      default:
        return '';
    }
  }

  getDomainStatusText(status: string): string {
    return this.#translateService.instant(`SETTINGS.GENERAL.DOMAINS.STATUS_${status}`);
  }

  hasCustomDomain(isClientApp: boolean): boolean {
    return !!this.getCustomDomainForType(isClientApp);
  }

  openTenantDialog(): void {
    const tenantValue = this.tenant();
    if (!tenantValue) return;

    const tenantForm = this.#fb.group({
      name: [
        tenantValue.name,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20),
          Validators.pattern(/^[\p{L}0-9][\p{L}0-9 ]*[\p{L}0-9]$/u),
        ],
      ],
    });

    const dialogRef = this.#dialog.open(TenantDialogComponent, {
      data: {
        form: tenantForm,
        tenant: tenantValue,
        title: this.#translateService.instant('SETTINGS.GENERAL.EDIT_TENANT'),
        cancelLabel: this.#translateService.instant('SETTINGS.GENERAL.CANCEL'),
        confirmLabel: this.#translateService.instant('SETTINGS.GENERAL.SAVE'),
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result && tenantForm.valid) {
        this.#generalService.updateTenant({ name: tenantForm.value.name as string });
      }
    });
  }

  openLanguageDialog(): void {
    const tenantValue = this.tenant();
    if (!tenantValue) return;

    // Use current language from translate service as fallback if tenant language not set
    const currentLang =
      tenantValue.preferredLanguage ||
      this.#translateService.currentLang ||
      this.#translateService.getDefaultLang();

    const languageForm = this.#fb.group({
      preferredLanguage: [currentLang, Validators.required],
    });

    const dialogRef = this.#dialog.open(LanguageDialogComponent, {
      data: {
        form: languageForm,
        languages: this.availableLanguages(),
        title: this.#translateService.instant('SETTINGS.GENERAL.EDIT_LANGUAGE'),
        cancelLabel: this.#translateService.instant('SETTINGS.GENERAL.CANCEL'),
        confirmLabel: this.#translateService.instant('SETTINGS.GENERAL.SAVE'),
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result && languageForm.valid) {
        const preferredLanguage = languageForm.value.preferredLanguage as string;
        this.#generalService.updateTenant({ preferredLanguage });
      }
    });
  }

  openAddDomainDialog(domainType: DomainType): void {
    // Check existing domains
    const hasClientDomain = this.hasCustomDomain(true);
    const hasMainDomain = this.hasCustomDomain(false);

    // If both domain types already exist, show a message instead
    if (hasClientDomain && hasMainDomain) {
      this.#toastr.info(
        this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.BOTH_TYPES_EXIST_MESSAGE'),
        this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.BOTH_TYPES_EXIST_TITLE')
      );
      return;
    }

    // Domain name pattern validator
    const domainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;

    // Set initial domain type based on availability
    const initialDomainType = hasClientDomain ? 'main' : (hasMainDomain ? 'client' : domainType);

    const domainForm = this.#fb.group({
      domain: ['', [Validators.required, Validators.pattern(domainPattern)]],
      domainType: [initialDomainType, Validators.required]
    });

    const dialogRef = this.#dialog.open(DomainDialogComponent, {
      data: {
        form: domainForm,
        domainType: domainType,
        title: this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.REQUEST'),
        subTitle: this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.CUSTOM_DESCRIPTION'),
        cancelLabel: this.#translateService.instant('SETTINGS.GENERAL.CANCEL'),
        confirmLabel: this.#translateService.instant('SETTINGS.GENERAL.REQUEST'),
        isUpdate: false,
        hasClientDomain: hasClientDomain,
        hasMainDomain: hasMainDomain
      },
      width: '32rem',
      panelClass: 'white-dialog'
    });

    dialogRef.closed.subscribe((result) => {
      if (result && domainForm.valid) {
        const domain = domainForm.value.domain as string;
        const selectedDomainType = domainForm.value.domainType as DomainType;
        const isClientApp = selectedDomainType === 'client';
        this.#domainService.createCustomDomain(domain, isClientApp);
      }
    });
  }

  updateDomain(domain: TenantDomain): void {
    // Domain name pattern validator
    const domainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;

    const domainForm = this.#fb.group({
      domain: [domain.domain, [Validators.required, Validators.pattern(domainPattern)]]
    });

    const domainType: DomainType = domain.isClientApp ? 'client' : 'main';

    const dialogRef = this.#dialog.open(DomainDialogComponent, {
      data: {
        form: domainForm,
        domainType: domainType,
        title: this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.UPDATE.TITLE'),
        subTitle: this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.UPDATE.DESCRIPTION'),
        cancelLabel: this.#translateService.instant('SETTINGS.GENERAL.CANCEL'),
        confirmLabel: this.#translateService.instant('SETTINGS.GENERAL.UPDATE'),
        isUpdate: true
      },
      width: '32rem',
      panelClass: 'white-dialog'
    });

    dialogRef.closed.subscribe((result) => {
      if (result && domainForm.valid) {
        const newDomain = domainForm.value.domain as string;
        this.#domainService.updateCustomDomain(domain.id, newDomain);
      }
    });
  }

  deleteDomain(domain: TenantDomain): void {
    if (confirm(this.#translateService.instant('SETTINGS.GENERAL.DOMAINS.DELETE.CONFIRM'))) {
      this.#domainService.deleteCustomDomain(domain.id);
    }
  }
}
